// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Eigene Übungen anlegen/ändern und persönliche Zettel (Video, Notiz) an
// Katalog-Übungen. Muster wie lib/plans.ts: direkt am Server, danach Sync —
// braucht Netz, wie das Anlegen von Plänen auch.
import { pb } from "./pb"
import { getExerciseNotes, syncFromPocketBase } from "./data"
import type { Exercise, ExerciseNote } from "./types"

export interface UebungsFelder {
  name: string
  body_part: string
  equipment: string
  execution_text: string
  video_url: string
  default_sets: number
  default_reps: string
}

function meineId(): string {
  return pb.authStore.record?.id ?? ""
}

/** Eigene Übung? (nur die darf man voll bearbeiten) */
export function istEigene(ex: Pick<Exercise, "owner">): boolean {
  return Boolean(ex.owner) && ex.owner === meineId()
}

/** Slug aus dem Namen — mit Zufallsanhang, weil der Index über ALLE Nutzer
 *  eindeutig ist: zwei Leute dürfen dieselbe Übung anlegen. */
function slugAus(name: string): string {
  const kern = name.toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "uebung"
  return `custom-${kern}-${crypto.randomUUID().slice(0, 6)}`
}

export async function createExercise(f: UebungsFelder): Promise<Exercise> {
  const ex = await pb.collection("exercises").create<Exercise>({
    name: f.name.trim(),
    slug: slugAus(f.name),
    source: "custom",
    owner: meineId(),
    body_part: f.body_part,
    target_muscle: "",
    equipment: f.equipment,
    instructions: "",
    benefit_text: "",
    execution_text: f.execution_text.trim(),
    video_search_term: f.name.trim(),
    video_url: f.video_url.trim(),
    default_sets: f.default_sets,
    default_reps: f.default_reps.trim(),
    media_slug: "",
  })
  await syncFromPocketBase()
  return ex
}

export async function updateExercise(id: string, f: Partial<UebungsFelder>): Promise<void> {
  const patch: Record<string, unknown> = {}
  if (f.name !== undefined) { patch.name = f.name.trim(); patch.video_search_term = f.name.trim() }
  if (f.body_part !== undefined) patch.body_part = f.body_part
  if (f.equipment !== undefined) patch.equipment = f.equipment
  if (f.execution_text !== undefined) patch.execution_text = f.execution_text.trim()
  if (f.video_url !== undefined) patch.video_url = f.video_url.trim()
  if (f.default_sets !== undefined) patch.default_sets = f.default_sets
  if (f.default_reps !== undefined) patch.default_reps = f.default_reps.trim()
  await pb.collection("exercises").update(id, patch)
  await syncFromPocketBase()
}

/** Persönlicher Zettel an einer fremden/kuratierten Übung: eigenes Video + Notiz.
 *  Leere Werte löschen den Zettel wieder. */
export async function saveNote(exerciseId: string, videoUrl: string, note: string): Promise<void> {
  const vorhanden = (await getExerciseNotes()).find((n) => n.exercise === exerciseId)
  const leer = !videoUrl.trim() && !note.trim()
  if (vorhanden && leer) {
    await pb.collection("exercise_notes").delete(vorhanden.id)
  } else if (vorhanden) {
    await pb.collection("exercise_notes").update(vorhanden.id, { video_url: videoUrl.trim(), note: note.trim() })
  } else if (!leer) {
    await pb.collection("exercise_notes").create<ExerciseNote>({
      owner: meineId(), exercise: exerciseId, video_url: videoUrl.trim(), note: note.trim(),
    })
  }
  await syncFromPocketBase()
}

/** Video für ALLE hinterlegen — Admins und Mitglieder mit Katalog-Recht
 *  (siehe lib/mitglieder.ts darfKuratieren). */
export async function setKatalogVideo(exerciseId: string, videoUrl: string): Promise<void> {
  await pb.collection("exercises").update(exerciseId, { video_url: videoUrl.trim() })
  await syncFromPocketBase()
}
